import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import type {
  DoorVisitCoreCallQuery,
  DoorVisitCoreCallQueryVariables,
} from '../graphql/queries/doorVisitCoreCall.generated';
import type {
  DoorVisitPlaybackMutation,
  DoorVisitPlaybackMutationVariables,
} from '../graphql/queries/doorVisitPlayback.generated';

export type DoorVisitCoreFetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type DoorVisitCoreRequestOptions = {
  signal?: AbortSignal;
};

export type DoorVisitCoreTranscriptionEvent = {
  speaker: string;
  occurredAt: unknown;
  transcript: string;
};

export type DoorVisitTranscriptResult =
  | { kind: 'available'; events: DoorVisitCoreTranscriptionEvent[] }
  | { kind: 'unavailable' }
  | { kind: 'error' }
  | { kind: 'aborted' };

export type DoorVisitPlaybackResult =
  | { kind: 'available'; playbackUrl: string }
  | { kind: 'unavailable' }
  | { kind: 'error' }
  | { kind: 'aborted' };

export type DoorVisitCoreApi = {
  getTranscript: (
    input: { leadId: string; callId: string },
    options?: DoorVisitCoreRequestOptions,
  ) => Promise<DoorVisitTranscriptResult>;
  getPlayback: (
    input: { leadId: string; callId: string },
    options?: DoorVisitCoreRequestOptions,
  ) => Promise<DoorVisitPlaybackResult>;
};

type AuthenticatedClient = ReturnType<typeof createAuthenticatedGraphQLClient>;
type AuthenticatedClientFactory = (options: Parameters<typeof createAuthenticatedGraphQLClient>[0]) => AuthenticatedClient;

export type CreateDoorVisitCoreApiOptions = {
  env?: RuntimeEnvironment;
  fetchImpl?: DoorVisitCoreFetchImpl;
  authenticatedClientFactory?: AuthenticatedClientFactory;
};

// Codegen emits operation types only. Keep these runtime documents synchronized
// with the matching source operations under graphql/queries.
export const DOOR_VISIT_CORE_CALL_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitCoreCall($leadId: ID!, $callId: String!) @api(name: core) {
    customer(id: $leadId) {
      callById(callId: $callId) {
        hasRecording
        transcription {
          speaker
          occurredAt
          transcript
        }
      }
    }
  }
`);

export const DOOR_VISIT_PLAYBACK_DOCUMENT = parse(/* GraphQL */ `
  mutation DoorVisitPlayback($leadId: ID!, $callId: ID!) @api(name: core) {
    playBackCall(callId: $callId, id: $leadId) {
      playbackUrl
    }
  }
`);

export const isNonBlankDoorVisitCoreString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isAborted = (signal: AbortSignal | undefined): boolean => signal?.aborted === true;

const unavailableTranscript = (): DoorVisitTranscriptResult => ({ kind: 'unavailable' });

const unavailablePlayback = (): DoorVisitPlaybackResult => ({ kind: 'unavailable' });

export const createDoorVisitCoreApi = ({
  env = resolveClientEnv(),
  fetchImpl,
  authenticatedClientFactory = createAuthenticatedGraphQLClient,
}: CreateDoorVisitCoreApiOptions = {}): DoorVisitCoreApi => {
  const client = authenticatedClientFactory({ env, fetchImpl });

  return {
    getTranscript: async ({ leadId, callId }, options = {}) => {
      if (!isNonBlankDoorVisitCoreString(leadId) || !isNonBlankDoorVisitCoreString(callId)) {
        return unavailableTranscript();
      }

      try {
        const variables: DoorVisitCoreCallQueryVariables = { leadId, callId };
        const result = await client.request<DoorVisitCoreCallQuery>({
          document: DOOR_VISIT_CORE_CALL_DOCUMENT,
          variables,
          operationName: 'DoorVisitCoreCall',
          signal: options.signal,
        });
        if (isAborted(options.signal)) return { kind: 'aborted' };

        const call = result.customer?.callById;
        if (call === null || call === undefined) return unavailableTranscript();

        return { kind: 'available', events: call.transcription ?? [] };
      } catch {
        return isAborted(options.signal) ? { kind: 'aborted' } : { kind: 'error' };
      }
    },
    getPlayback: async ({ leadId, callId }, options = {}) => {
      if (!isNonBlankDoorVisitCoreString(leadId) || !isNonBlankDoorVisitCoreString(callId)) {
        return unavailablePlayback();
      }

      try {
        const variables: DoorVisitPlaybackMutationVariables = { leadId, callId };
        const result = await client.request<DoorVisitPlaybackMutation>({
          document: DOOR_VISIT_PLAYBACK_DOCUMENT,
          variables,
          operationName: 'DoorVisitPlayback',
          signal: options.signal,
        });
        if (isAborted(options.signal)) return { kind: 'aborted' };

        const playbackUrl = result.playBackCall?.playbackUrl;
        if (!isNonBlankDoorVisitCoreString(playbackUrl)) return unavailablePlayback();

        return { kind: 'available', playbackUrl: playbackUrl.trim() };
      } catch {
        return isAborted(options.signal) ? { kind: 'aborted' } : { kind: 'error' };
      }
    },
  };
};
